import { salesRpc, fetchAllSales, financialSummary } from '@/lib/salesOverview';
import type { SaleWithCustomer } from '@/hooks/useSales';
import type { OverviewAnalyticsResponse } from '@/hooks/useSalesAnalytics';
import {
  salesBusinessDate,
  parseCalendarDate,
  purchaseBalance,
  purchasePaymentState,
  type PurchaseRecordMetadata,
} from '@klothiq/domain';
// ─────────────────────────────────────────────
// KLOTHIQ — Report Data Hooks
// FEAT-REPORTS-001 §6 API Contract
// ─────────────────────────────────────────────
// One hook per report type. Each returns chartData, tableData, and insights.

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import type { DateRangeFilter, ReportInsight } from './reportTypes';

// ── Helpers ───────────────────────────────────────────────────

function fmt(n: number): string {
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function pct(n: number): string {
  return `${n >= 0 ? '' : '-'}${Math.abs(n).toFixed(1)}%`;
}

function toMonthKey(dateStr: string): string {
  return dateStr.slice(0, 7); // YYYY-MM
}

const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function monthLabel(key: string): string {
  const [, m] = key.split('-');
  return MONTH_SHORT[parseInt(m!, 10) - 1] ?? key;
}

function dayLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return `${d.getDate()} ${MONTH_SHORT[d.getMonth()]}`;
}

/** Determine granularity from date range span */
function getGranularity(from: string, to: string): 'daily' | 'monthly' {
  const diff = (new Date(to).getTime() - new Date(from).getTime()) / (1000 * 60 * 60 * 24);
  return diff <= 45 ? 'daily' : 'monthly';
}

// ── 1. Sales Summary ──────────────────────────────────────────

export function useSalesSummaryReport(dateRange: DateRangeFilter) {
  const { organization, profile, access, can } = useAuth();
  return useQuery({
    queryKey: [
      'report',
      'sales',
      'summary',
      organization?.id,
      profile?.id,
      access?.access_version,
      dateRange,
    ],
    enabled:
      !!organization && !!dateRange.from && !!dateRange.to && can('report_sales_summary.view'),
    staleTime: 15000,
    refetchInterval: 60000,
    queryFn: async () => {
      const result = await salesRpc<OverviewAnalyticsResponse>('get_sales_overview_analytics', {
        p_filters: dateRange,
      });
      const financial = can('financials.view') && result.totals.profit !== undefined;
      const tableData = result.buckets.map((b) => ({
        date: b.key,
        label: dayLabel(b.key),
        orderCount: Number(b.orders),
        grossRevenue: Number(b.sales),
        ...(financial
          ? {
              cogs: Number(b.sales) - Number(b.profit),
              grossProfit: Number(b.profit),
              marginPercent: Number(b.sales) > 0 ? (Number(b.profit) / Number(b.sales)) * 100 : 0,
            }
          : {}),
        collected: Number(b.collected),
        balanceDue: Number(b.balance_due),
      }));
      const chartData = tableData.map((b) => ({
        key: b.date,
        label: b.label,
        revenue: b.grossRevenue,
        ...(financial ? { cogs: b.cogs, profit: b.grossProfit } : {}),
      }));
      const insights: ReportInsight[] = [
        {
          label: 'Invoice value (before return adjustment)',
          value: fmt(result.totals.sales),
          alertType: 'neutral',
        },
        { label: 'Orders', value: String(result.totals.orders), alertType: 'neutral' },
        {
          label: 'Receipts by payment date',
          value: fmt(result.totals.receipts),
          alertType: 'emerald',
        },
        {
          label: 'Returns requiring balance review',
          value: String(result.totals.return_review_count),
          alertType: result.totals.return_review_count ? 'amber' : 'neutral',
        },
      ];
      return { chartData, tableData, insights };
    },
  });
}

// ── 2. Sales by Product / Team ────────────────────────────────
// Strategy: Query sales in date range first, then query sale_items
// for those sales. This avoids complex inner-join filtering issues.

export function useSalesByProductReport(dateRange: DateRangeFilter, groupBy: string = 'product') {
  const { organization, profile, access, can } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [
      'report',
      'sales',
      'by-product',
      orgId,
      profile?.id,
      access?.access_version,
      dateRange,
      groupBy,
    ],
    queryFn: async () => {
      const salesInRange = await fetchAllSales<SaleWithCustomer>({
        from: dateRange.from,
        to: dateRange.to,
      });
      const saleIds = salesInRange
        .filter((s) => !['draft', 'cancelled', 'returned'].includes(s.sale_status))
        .map((s) => s.id);
      if (saleIds.length === 0) {
        return {
          chartData: [],
          tableData: [],
          insights: [
            { label: 'Total Revenue', value: fmt(0), alertType: 'neutral' as const },
            { label: 'Total Units Sold', value: '0', alertType: 'neutral' as const },
            { label: 'Unique Items', value: '0', alertType: 'neutral' as const },
            { label: 'Avg Margin', value: pct(0), alertType: 'neutral' as const },
          ],
        };
      }

      // Step 2: Get sale items for those sales with product info
      const rows: Array<{
        quantity: number;
        unit_price: number;
        line_total: number;
        unit_cost: number;
        product_variants: unknown;
      }> = [];
      for (let start = 0; start < saleIds.length; start += 200) {
        let offset = 0;
        while (true) {
          const { data, error, count } = await supabase
            .from('sale_items')
            .select(
              'id,quantity,unit_price,line_total,unit_cost,product_variants(sku,size,color,products(id,name,product_code,team,product_type_id,product_types(name)))',
              { count: 'exact' },
            )
            .eq('organization_id', orgId!)
            .in('sale_id', saleIds.slice(start, start + 200))
            .order('id')
            .range(offset, offset + 999);
          if (error) throw error;
          rows.push(...(data ?? []));
          offset += (data ?? []).length;
          if (count !== null && offset >= count) break;
          if (!data?.length)
            throw new Error('The product sales list changed. Refresh and try again.');
        }
      }

      // Group by product, team, or category
      const groupMap = new Map<
        string,
        {
          name: string;
          code: string;
          unitsSold: number;
          totalRevenue: number;
          totalCost: number;
        }
      >();

      for (const item of rows) {
        const variant = item.product_variants as any;
        const product = variant?.products as any;
        if (!product) continue;

        let groupKey: string;
        let groupName: string;
        let groupCode: string;

        if (groupBy === 'category') {
          groupKey = product.product_type_id || 'unclassified';
          groupName = product.product_types?.name || 'Unclassified';
          groupCode = '';
        } else if (groupBy === 'team') {
          groupKey = product.team || 'No Team';
          groupName = groupKey;
          groupCode = '';
        } else {
          // Default: group by product name
          groupKey = product.id || product.product_code || 'unknown';
          groupName = product.name || 'Unknown';
          groupCode = product.product_code || '';
        }

        if (!groupMap.has(groupKey)) {
          groupMap.set(groupKey, {
            name: groupName,
            code: groupCode,
            unitsSold: 0,
            totalRevenue: 0,
            totalCost: 0,
          });
        }
        const g = groupMap.get(groupKey)!;
        g.unitsSold += Number(item.quantity) || 0;
        g.totalRevenue += Number(item.line_total) || 0;
        g.totalCost += (Number(item.unit_cost) || 0) * (Number(item.quantity) || 0);
      }

      // Sort by revenue descending
      const sorted = Array.from(groupMap.values()).sort((a, b) => b.totalRevenue - a.totalRevenue);

      const chartData = sorted.slice(0, 10).map((g) => ({
        name: g.name.length > 20 ? g.name.slice(0, 18) + '…' : g.name,
        fullName: g.name,
        revenue: Math.round(g.totalRevenue * 100) / 100,
        cost: Math.round(g.totalCost * 100) / 100,
      }));

      const tableData = sorted.map((g) => ({
        name: g.name,
        code: g.code,
        unitsSold: g.unitsSold,
        avgSellingPrice: g.unitsSold > 0 ? g.totalRevenue / g.unitsSold : 0,
        totalRevenue: g.totalRevenue,
        totalCost: g.totalCost,
        grossMarginPercent:
          g.totalRevenue > 0 ? ((g.totalRevenue - g.totalCost) / g.totalRevenue) * 100 : 0,
      }));

      const totalRev = sorted.reduce((s, g) => s + g.totalRevenue, 0);
      const totalCost = sorted.reduce((s, g) => s + g.totalCost, 0);
      const totalUnits = sorted.reduce((s, g) => s + g.unitsSold, 0);

      const insights: ReportInsight[] = [
        { label: 'Total Revenue', value: fmt(totalRev), alertType: 'neutral' },
        {
          label: 'Total Units Sold',
          value: totalUnits.toLocaleString('en-IN'),
          alertType: 'neutral',
        },
        {
          label: groupBy === 'category' ? 'Categories' : groupBy === 'team' ? 'Teams' : 'Products',
          value: String(sorted.length),
          alertType: 'neutral',
        },
        {
          label: 'Avg Margin',
          value: pct(totalRev > 0 ? ((totalRev - totalCost) / totalRev) * 100 : 0),
          alertType: totalRev > totalCost ? 'emerald' : 'red',
        },
      ];

      return { chartData, tableData, insights };
    },
    enabled: !!orgId && can('report_sales_by_product.view') && !!dateRange.from && !!dateRange.to,
    staleTime: 15000,
    refetchInterval: 60000,
  });
}

// ── 3. Stock Valuation & Low Stock ────────────────────────────

export function useStockValuationReport(statusFilter: string = 'all') {
  const { organization, profile, access, can } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [
      'report',
      'inventory',
      'valuation',
      orgId,
      profile?.id,
      access?.access_version,
      statusFilter,
    ],
    queryFn: async () => {
      let query = supabase.from('v_inventory_summary').select('*').eq('organization_id', orgId!);

      if (statusFilter === 'low_stock') {
        query = query.eq('stock_status', 'low_stock');
      } else if (statusFilter === 'out_of_stock') {
        query = query.eq('stock_status', 'out_of_stock');
      }

      const { data: inventory, error } = await query.order('product_name', { ascending: true });
      if (error) throw error;
      const rows = inventory ?? [];

      // Group by stock status for chart
      const statusGroups = new Map<
        string,
        { label: string; count: number; costValue: number; retailValue: number }
      >();
      const statusLabels: Record<string, string> = {
        in_stock: 'In Stock',
        low_stock: 'Low Stock',
        out_of_stock: 'Out of Stock',
      };

      let totalCostValue = 0;
      let totalRetailValue = 0;
      let totalUnits = 0;

      for (const r of rows) {
        const status = (r.stock_status as string) || 'in_stock';
        const qty = Number(r.quantity_on_hand) || 0;
        const wac = Number(r.weighted_avg_cost) || 0;
        const price = Number(r.default_price) || 0;
        const costVal = qty * wac;
        const retailVal = qty * price;

        if (!statusGroups.has(status)) {
          statusGroups.set(status, {
            label: statusLabels[status] || status,
            count: 0,
            costValue: 0,
            retailValue: 0,
          });
        }
        const g = statusGroups.get(status)!;
        g.count += 1;
        g.costValue += costVal;
        g.retailValue += retailVal;

        totalCostValue += costVal;
        totalRetailValue += retailVal;
        totalUnits += qty;
      }

      const chartData = Array.from(statusGroups.entries()).map(([key, g]) => ({
        name: g.label,
        status: key,
        count: g.count,
        costValue: Math.round(g.costValue),
        retailValue: Math.round(g.retailValue),
      }));

      const tableData = rows.map((r) => ({
        sku: r.sku,
        productName: r.product_name,
        sizeColor: [r.size, r.color].filter(Boolean).join(' / '),
        onHand: Number(r.quantity_on_hand) || 0,
        available: Number(r.quantity_available) || 0,
        unitCost: Number(r.weighted_avg_cost) || 0,
        retailPrice: Number(r.default_price) || 0,
        totalCostValue: (Number(r.quantity_on_hand) || 0) * (Number(r.weighted_avg_cost) || 0),
        totalRetailValue: (Number(r.quantity_on_hand) || 0) * (Number(r.default_price) || 0),
        status: r.stock_status,
      }));

      const lowStockCount = rows.filter((r) => r.stock_status === 'low_stock').length;
      const outOfStockCount = rows.filter((r) => r.stock_status === 'out_of_stock').length;

      const insights: ReportInsight[] = [
        { label: 'Total SKUs', value: String(rows.length), alertType: 'neutral' },
        { label: 'Total Units', value: totalUnits.toLocaleString('en-IN'), alertType: 'neutral' },
        { label: 'Cost Valuation', value: fmt(totalCostValue), alertType: 'neutral' },
        {
          label: 'Low / OOS',
          value: `${lowStockCount} / ${outOfStockCount}`,
          alertType: lowStockCount + outOfStockCount > 0 ? 'amber' : 'emerald',
        },
      ];

      return { chartData, tableData, insights };
    },
    enabled: !!orgId && can('report_stock_valuation.view'),
    staleTime: 15000,
    refetchInterval: 60000,
  });
}

// ── 4. Receivables Aging ──────────────────────────────────────

export function useReceivablesAgingReport() {
  const { organization, profile, access, can } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [
      'report',
      'financial',
      'receivables-aging',
      orgId,
      profile?.id,
      access?.access_version,
    ],
    queryFn: async () => {
      const sales = await fetchAllSales<SaleWithCustomer>({});
      const reviewCount = sales.filter((s) => financialSummary(s).return_review).length;
      const rows = sales
        .filter((s) => (financialSummary(s).amount_due ?? 0) > 0)
        .map((s) => ({
          ...s,
          customer_phone: s.customer_mobile || s.customers?.phone,
          balance_due: financialSummary(s).amount_due!,
        }));
      const now = parseCalendarDate(salesBusinessDate(organization?.timezone))!;
      // Group by customer and bucket by aging
      const customerMap = new Map<
        string,
        {
          customerName: string;
          customerPhone: string | null;
          current: number;
          thirty: number;
          sixty: number;
          ninety: number;
          totalBalance: number;
          oldestDate: string;
          orderCount: number;
        }
      >();

      for (const r of rows) {
        const custId = r.customer_id || `sale:${r.id}`;
        const custName = r.customer_name || 'Walk-in Customer';

        if (!customerMap.has(custId)) {
          customerMap.set(custId, {
            customerName: custName,
            customerPhone: r.customer_phone || null,
            current: 0,
            thirty: 0,
            sixty: 0,
            ninety: 0,
            totalBalance: 0,
            oldestDate: r.sale_date || '',
            orderCount: 0,
          });
        }
        const c = customerMap.get(custId)!;
        const balance = Number(r.balance_due) || 0;
        const saleDate = parseCalendarDate(r.sale_date) || now;
        const daysDiff = Math.floor((now.getTime() - saleDate.getTime()) / (1000 * 60 * 60 * 24));

        if (daysDiff <= 30) c.current += balance;
        else if (daysDiff <= 60) c.thirty += balance;
        else if (daysDiff <= 90) c.sixty += balance;
        else c.ninety += balance;

        c.totalBalance += balance;
        c.orderCount += 1;
        if (!c.oldestDate || r.sale_date < c.oldestDate) {
          c.oldestDate = r.sale_date || '';
        }
      }

      const customers = Array.from(customerMap.values()).sort(
        (a, b) => b.totalBalance - a.totalBalance,
      );

      // Chart: summary by aging bucket
      const totalCurrent = customers.reduce((s, c) => s + c.current, 0);
      const totalThirty = customers.reduce((s, c) => s + c.thirty, 0);
      const totalSixty = customers.reduce((s, c) => s + c.sixty, 0);
      const totalNinety = customers.reduce((s, c) => s + c.ninety, 0);

      const chartData = [
        { bucket: '0–30 Days', amount: Math.round(totalCurrent) },
        { bucket: '31–60 Days', amount: Math.round(totalThirty) },
        { bucket: '61–90 Days', amount: Math.round(totalSixty) },
        { bucket: '90+ Days', amount: Math.round(totalNinety) },
      ];

      const tableData = customers.map((c) => ({
        customerName: c.customerName,
        phone: c.customerPhone || '—',
        ordersOverdue: c.orderCount,
        current: c.current,
        thirtyDays: c.thirty,
        sixtyDays: c.sixty,
        ninetyPlus: c.ninety,
        totalBalance: c.totalBalance,
        oldestInvoice: c.oldestDate,
      }));

      const totalBalance = customers.reduce((s, c) => s + c.totalBalance, 0);

      const insights: ReportInsight[] = [
        {
          label: 'Total Outstanding',
          value: fmt(totalBalance),
          alertType: totalBalance > 0 ? 'red' : 'emerald',
        },
        {
          label: 'Returned orders requiring review (excluded)',
          value: String(reviewCount),
          alertType: reviewCount ? 'amber' : 'neutral',
        },
        { label: 'Current (0–30d)', value: fmt(totalCurrent), alertType: 'neutral' },
        {
          label: 'Overdue (90d+)',
          value: fmt(totalNinety),
          alertType: totalNinety > 0 ? 'red' : 'emerald',
        },
      ];

      return { chartData, tableData, insights };
    },
    enabled: !!orgId && can('report_receivables.view'),
    staleTime: 15000,
    refetchInterval: 60000,
  });
}

// ── 5. Purchases & Payables ───────────────────────────────────

export function usePurchasesReport(dateRange: DateRangeFilter, statusFilter: string = 'all') {
  const { organization, profile, access, can } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [
      'report',
      'purchases',
      'summary',
      orgId,
      profile?.id,
      access?.access_version,
      dateRange,
      statusFilter,
    ],
    queryFn: async () => {
      const allRows: Array<
        {
          id: string;
          purchase_number: string;
          purchase_date: string;
          status: string;
          total_amount: number;
          paid_amount: number;
          balance_due: number;
          payment_status: string;
          suppliers: { name: string } | Array<{ name: string }> | null;
        } & Partial<PurchaseRecordMetadata>
      > = [];
      for (let offset = 0; ; offset += 500) {
        let query = supabase
          .from('purchases')
          .select(
            `
          *,
          suppliers ( name )
        `,
          )
          .eq('organization_id', orgId!)
          .eq('is_voided', false)
          .in('status', ['ordered', 'partially_received', 'received'])
          .gte('purchase_date', dateRange.from)
          .lte('purchase_date', dateRange.to)
          .order('purchase_date', { ascending: true })
          .order('id')
          .range(offset, offset + 499);

        if (statusFilter === 'unpaid') {
          query = query.in('payment_status', ['unpaid', 'partially_paid']);
        }

        const { data: purchases, error } = await query;
        if (error) throw error;
        allRows.push(...(purchases || []));
        if (!purchases || purchases.length < 500) break;
      }
      const rows = allRows.filter(
        (p) =>
          !p.is_deleted &&
          (statusFilter !== 'unpaid' ||
            (purchaseBalance(p) !== null && Number(purchaseBalance(p)) > 0)),
      );

      const granularity = getGranularity(dateRange.from, dateRange.to);
      const bucketMap = new Map<
        string,
        {
          label: string;
          purchaseAmount: number;
          paidAmount: number;
          balanceDue: number;
          count: number;
        }
      >();

      for (const p of rows) {
        const key =
          granularity === 'daily'
            ? p.purchase_date?.slice(0, 10)
            : toMonthKey(p.purchase_date ?? '');
        if (!key) continue;
        if (!bucketMap.has(key)) {
          bucketMap.set(key, {
            label: granularity === 'daily' ? dayLabel(key) : monthLabel(key),
            purchaseAmount: 0,
            paidAmount: 0,
            balanceDue: 0,
            count: 0,
          });
        }
        const b = bucketMap.get(key)!;
        b.purchaseAmount += Number(p.total_amount) || 0;
        b.paidAmount += Number(p.paid_amount) || 0;
        b.balanceDue += purchaseBalance(p) ?? 0;
        b.count += 1;
      }

      const chartData = Array.from(bucketMap.entries()).map(([key, b]) => ({
        key,
        label: b.label,
        purchaseAmount: Math.round(b.purchaseAmount * 100) / 100,
        paidAmount: Math.round(b.paidAmount * 100) / 100,
        balanceDue: Math.round(b.balanceDue * 100) / 100,
      }));

      const tableData = rows.map((p) => {
        const supplier = Array.isArray(p.suppliers) ? p.suppliers[0] : p.suppliers;
        return {
          poNumber: p.purchase_number,
          date: p.purchase_date,
          supplierName: supplier?.name || '—',
          totalAmount: Number(p.total_amount) || 0,
          paidAmount: Number(p.paid_amount) || 0,
          balanceDue: purchaseBalance(p),
          paymentStatus: purchasePaymentState(p),
        };
      });

      const totalAmount = rows.reduce((s, p) => s + (Number(p.total_amount) || 0), 0);
      const totalPaid = rows.reduce((s, p) => s + (Number(p.paid_amount) || 0), 0);
      const totalBalance = rows.reduce((s, p) => s + (purchaseBalance(p) ?? 0), 0);

      const insights: ReportInsight[] = [
        { label: 'Total Purchases', value: fmt(totalAmount), alertType: 'neutral' },
        { label: 'Paid on these orders', value: fmt(totalPaid), alertType: 'emerald' },
        {
          label: 'Tracked outstanding',
          value: fmt(totalBalance),
          alertType: totalBalance > 0 ? 'amber' : 'emerald',
        },
        { label: 'PO Count', value: String(rows.length), alertType: 'neutral' },
      ];

      return { chartData, tableData, insights };
    },
    enabled: !!orgId && can('report_purchases.view') && !!dateRange.from && !!dateRange.to,
    staleTime: 15000,
    refetchInterval: 60000,
  });
}

// ── 6. Returns Summary ────────────────────────────────────────

export function useReturnsSummaryReport(dateRange: DateRangeFilter) {
  const { organization, profile, access, can } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [
      'report',
      'returns',
      'summary',
      orgId,
      profile?.id,
      access?.access_version,
      dateRange,
    ],
    queryFn: async () => {
      const sales = await fetchAllSales<SaleWithCustomer>({
        from: dateRange.from,
        to: dateRange.to,
      });
      const rows = sales.filter((s) => s.return_status || financialSummary(s).return_review);
      // Group by return/sale status for chart
      const statusMap = new Map<string, { count: number; value: number }>();
      // return_status values from migration 00035: requested, accepted, rejected, completed
      const statusLabels: Record<string, string> = {
        requested: 'Requested',
        accepted: 'Accepted',
        rejected: 'Rejected',
        completed: 'Completed',
        partially_returned: 'Partial Return',
        returned: 'Fully Returned',
      };

      for (const s of rows) {
        const status = s.return_status || s.sale_status || 'unknown';
        if (!statusMap.has(status)) {
          statusMap.set(status, { count: 0, value: 0 });
        }
        const g = statusMap.get(status)!;
        g.count += 1;
        g.value += Number(s.total_amount) || 0;
      }

      const chartData = Array.from(statusMap.entries()).map(([key, g]) => ({
        status: statusLabels[key] || key,
        statusKey: key,
        count: g.count,
        value: Math.round(g.value),
      }));

      const tableData = rows.map((s) => ({
        saleNumber: s.sale_number,
        date: s.sale_date,
        customer: s.customer_name || 'Walk-in',
        returnStatus: statusLabels[s.return_status || ''] || s.return_status || '—',
        saleStatus: s.sale_status,
        originalInvoiceValue: Number(s.total_amount) || 0,
        returnedUnits: s.returned_units ?? 0,
        refunded: financialSummary(s).refunded,
        refundStatus:
          financialSummary(s).refunded > 0
            ? 'Refund recorded'
            : financialSummary(s).return_review
              ? 'Review refund requirement'
              : 'No refund recorded',
      }));

      const totalReturns = rows.length;
      const totalValue = rows.reduce((s, r) => s + (Number(r.total_amount) || 0), 0);
      const completed = rows.filter(
        (r) =>
          r.return_status === 'completed' ||
          ['returned', 'partially_returned'].includes(r.sale_status),
      ).length;

      const insights: ReportInsight[] = [
        {
          label: 'Total Returns',
          value: String(totalReturns),
          alertType: totalReturns > 0 ? 'amber' : 'emerald',
        },
        {
          label: 'Original invoices with return activity',
          value: fmt(totalValue),
          alertType: 'neutral',
        },
        { label: 'Completed', value: String(completed), alertType: 'emerald' },
        {
          label: 'Pending',
          value: String(
            rows.filter((s) => ['requested', 'accepted'].includes(s.return_status ?? '')).length,
          ),
          alertType: totalReturns - completed > 0 ? 'amber' : 'emerald',
        },
      ];

      return { chartData, tableData, insights };
    },
    enabled: !!orgId && can('report_returns.view') && !!dateRange.from && !!dateRange.to,
    staleTime: 15000,
    refetchInterval: 60000,
  });
}
